import { useCallback, useEffect, useState, useMemo, Fragment } from "react";
import api from "@/lib/api";
import { formatCurrency } from "@/lib/format";
import { useCurrencyRail } from "@/context/CurrencyRailContext";
import ConsolidatedRailCard from "@/components/ConsolidatedRailCard";
import DrillDownModal from "@/components/DrillDownModal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
  SelectGroup, SelectLabel,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  TrendingDown, TrendingUp, Scale, BookOpen, FileSpreadsheet,
  Download, Plus, Trash2, ArrowDownUp, Sparkles,
  CheckSquare, Square, ChevronDown, Building2, Tag, X, Users,
} from "lucide-react";
import { startOfMonth, endOfMonth, startOfYear, endOfYear } from "date-fns";
import { toast } from "sonner";
import { DateField, fmtISO, downloadStream, CsvButton, BalancedBadge } from "./_shared";
import { useEntity } from "@/context/EntityContext";

const GL_PAGE = 100;

export function GeneralLedgerReport({ currency }) {
  const [start, setStart] = useState(startOfMonth(new Date()));
  const [end, setEnd] = useState(endOfMonth(new Date()));
  const [account, setAccount] = useState("__all__");
  const [categories, setCategories] = useState({ expense: [], income: [], saving: [] });
  const [wallets, setWallets] = useState([]);
  const [data, setData] = useState(null);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const { isActive, activeRail } = useCurrencyRail();
  // Bulk selection
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [moveOpen, setMoveOpen] = useState(false);
  const [moveTargetId, setMoveTargetId] = useState("");
  const [recatOpen, setRecatOpen] = useState(false);
  const [recatCategory, setRecatCategory] = useState("");
  const { entities, currentEntityId, currentEntity } = useEntity();
  const isBusiness = currentEntity?.workspace_type === "business";
  const catLabel = isBusiness ? "department" : "category";

  // Memoised derived lists — recomputed only when their source data or the
  // active rail changes, saving a filter+map on every render (audit iter 60f).
  const visibleEntries = useMemo(
    () => (data?.entries || []).filter((e) => isActive(e.currency || "USD")),
    [data, isActive]
  );
  const visibleCurrencyTotals = useMemo(
    () => (data?.by_currency || []).filter((r) => isActive(r.currency)),
    [data, isActive]
  );
  const moveTargetEntities = useMemo(
    () => entities.filter((e) => e.id !== currentEntityId),
    [entities, currentEntityId]
  );

  useEffect(() => {
    (async () => {
      const [{ data: cats }, { data: accs }] = await Promise.all([
        api.get("/categories"),
        api.get("/accounts"),
      ]);
      setCategories(cats);
      setWallets(accs);
    })();
  }, []);

  const load = useCallback(async (nextOffset = 0, append = false) => {
    setLoading(true);
    try {
      const params = { start: fmtISO(start), end: fmtISO(end), limit: GL_PAGE, offset: nextOffset };
      if (account && account !== "__all__") params.account = account;
      const { data: fresh } = await api.get("/accounting/general-ledger", { params });
      if (append && data) {
        setData({ ...fresh, entries: [...data.entries, ...fresh.entries] });
      } else {
        setData(fresh);
      }
      setOffset(nextOffset);
    } finally { setLoading(false); }
  }, [start, end, account, data]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setOffset(0); setSelectedIds(new Set()); load(0, false); }, [start, end, account]);

  // ---- Bulk selection helpers ----
  const enterSelectMode = () => setSelectMode(true);
  const cancelSelectMode = () => { setSelectMode(false); setSelectedIds(new Set()); };
  const toggleOne = (id) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelectedIds(next);
  };
  const entries = data?.entries || [];
  const allSelected = entries.length > 0 && entries.every((e) => selectedIds.has(e.id));
  const toggleAll = () => {
    if (allSelected) {
      const next = new Set(selectedIds);
      entries.forEach((e) => next.delete(e.id));
      setSelectedIds(next);
    } else {
      const next = new Set(selectedIds);
      entries.forEach((e) => next.add(e.id));
      setSelectedIds(next);
    }
  };

  const showUndoToast = ({ message, opId }) => {
    if (!opId) { toast.success(message); return; }
    toast.success(message, {
      duration: 5000,
      action: {
        label: "Undo",
        onClick: async () => {
          try {
            const { data: r } = await api.post(`/transactions/bulk-undo/${opId}`);
            toast.success(`Undone · ${r.restored} restored`);
            load(offset, false);
          } catch (err) {
            toast.error(err.response?.data?.detail || "Undo failed");
          }
        },
      },
    });
  };

  const doBulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    try {
      const { data: r } = await api.post("/transactions/bulk-delete", { ids });
      showUndoToast({ message: `Deleted ${r.deleted} entry${r.deleted === 1 ? "" : "s"}`, opId: r.op_id });
      cancelSelectMode();
      setBulkDeleteOpen(false);
      load(0, false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  const doBulkMove = async () => {
    const ids = Array.from(selectedIds);
    if (!ids.length || !moveTargetId) return;
    try {
      const { data: r } = await api.post("/transactions/bulk-move", { ids, target_entity_id: moveTargetId });
      showUndoToast({ message: `Moved ${r.moved} to ${r.target_entity_name}`, opId: r.op_id });
      cancelSelectMode();
      setMoveOpen(false);
      setMoveTargetId("");
      load(0, false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Move failed");
    }
  };

  const doBulkRecategorize = async () => {
    const ids = Array.from(selectedIds);
    if (!ids.length || !recatCategory) return;
    try {
      const { data: r } = await api.post("/transactions/bulk-recategorize", { ids, category: recatCategory });
      showUndoToast({ message: `Recategorized ${r.updated} to ${r.category}`, opId: r.op_id });
      cancelSelectMode();
      setRecatOpen(false);
      setRecatCategory("");
      load(0, false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Recategorize failed");
    }
  };

  return (
    <div data-testid="gl-report" className="space-y-6">
      <div className="flex flex-wrap items-end gap-3 justify-between">
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-[260px]">
            <div className="text-[10px] tracking-[0.2em] uppercase text-[var(--muted-text)] mb-1">Ledger account</div>
            <Select value={account} onValueChange={setAccount}>
              <SelectTrigger data-testid="gl-account-select" className="h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All transactions</SelectItem>
                {wallets.length > 0 && (
                  <SelectGroup>
                    <SelectLabel>Wallets</SelectLabel>
                    {wallets.map((w) => (
                      <SelectItem key={w.id} value={`wallet:${w.id}`}>{w.name}</SelectItem>
                    ))}
                  </SelectGroup>
                )}
                {["income", "expense", "saving"].map((t) => (
                  (categories[t] || []).length > 0 && (
                    <SelectGroup key={t}>
                      <SelectLabel className="capitalize">{t} categories</SelectLabel>
                      {(categories[t] || []).map((c) => (
                        <SelectItem key={`${t}-${c.id}`} value={c.name}>
                          <span className="inline-flex items-center gap-2">
                            <span style={{ color: c.color }}>{c.icon}</span>{c.name}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  )
                ))}
              </SelectContent>
            </Select>
          </div>
          <DateField label="From" value={start} onChange={setStart} testId="gl-start" />
          <DateField label="To" value={end} onChange={setEnd} testId="gl-end" />
        </div>
        <div className="flex gap-2">
          {!selectMode ? (
            <Button variant="outline" size="sm" onClick={enterSelectMode} data-testid="gl-select-mode-btn">
              <CheckSquare className="w-4 h-4 mr-2" /> Select
            </Button>
          ) : (
            <Button variant="outline" size="sm" onClick={cancelSelectMode} data-testid="gl-select-cancel-btn">
              <X className="w-4 h-4 mr-2" /> Cancel
            </Button>
          )}
          <CsvButton testId="gl-csv" onClick={() => {
            const p = { start: fmtISO(start), end: fmtISO(end) };
            if (account !== "__all__") p.account = account;
            downloadStream("/accounting/general-ledger.csv", p, "general_ledger.csv");
          }} />
        </div>
      </div>

      {selectMode && (
        <div
          data-testid="gl-bulk-bar"
          className="card-flat p-3 border-l-4 border-l-[#E07A5F] flex items-center justify-between gap-3 flex-wrap"
        >
          <div className="flex items-center gap-3">
            <button type="button" data-testid="gl-bulk-select-all" onClick={toggleAll} className="inline-flex items-center gap-2 text-sm">
              {allSelected ? <CheckSquare className="w-4 h-4 text-[#2F4F4F]" /> : <Square className="w-4 h-4 text-[var(--muted-text)]" />}
              {allSelected ? "Deselect visible" : "Select visible"}
            </button>
            <span data-testid="gl-bulk-selected-count" className="text-sm text-[var(--muted-text)]">{selectedIds.size} selected</span>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                data-testid="gl-bulk-actions-btn"
                disabled={selectedIds.size === 0}
                className="rounded-full bg-[#2F4F4F] hover:bg-[#213838] text-[#FDFCF8] h-9 px-4 gap-2"
              >
                Bulk actions{selectedIds.size > 0 ? ` (${selectedIds.size})` : ""}
                <ChevronDown className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem data-testid="gl-bulk-move" onClick={() => { setMoveTargetId(""); setMoveOpen(true); }}>
                <Building2 className="w-4 h-4 mr-2" /> Move to entity…
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="gl-bulk-recat" onClick={() => { setRecatCategory(""); setRecatOpen(true); }}>
                <Tag className="w-4 h-4 mr-2" /> Change {catLabel}…
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                data-testid="gl-bulk-delete"
                onClick={() => setBulkDeleteOpen(true)}
                className="text-[#E63946] focus:text-[#E63946] focus:bg-[#FCE7E9]"
              >
                <Trash2 className="w-4 h-4 mr-2" /> Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}

      {loading && !data && <div className="text-sm text-[var(--muted-text)]">Loading…</div>}
      {data && (
        <>
          <div className="grid grid-cols-3 gap-4">
            <div className="card-flat p-4">
              <div className="text-[10px] tracking-[0.2em] uppercase text-[var(--muted-text)]">Opening balance</div>
              <div data-testid="gl-opening" className="font-mono-data text-lg mt-1">{formatCurrency(data.opening_balance, data?.currency || currency)}</div>
            </div>
            <div className="card-flat p-4">
              <div className="text-[10px] tracking-[0.2em] uppercase text-[var(--muted-text)]">Period activity</div>
              <div className="font-mono-data text-lg mt-1">
                <span className="text-[#2F6B4A]">{formatCurrency(data.totals.debit, data?.currency || currency)}</span>
                <span className="text-[var(--muted-text)]"> / </span>
                <span className="text-[#B4283A]">{formatCurrency(data.totals.credit, data?.currency || currency)}</span>
              </div>
            </div>
            <div className="card-flat p-4">
              <div className="text-[10px] tracking-[0.2em] uppercase text-[var(--muted-text)]">Closing balance</div>
              <div data-testid="gl-closing" className="font-mono-data text-lg mt-1">{formatCurrency(data.totals.closing_balance, data?.currency || currency)}</div>
            </div>
          </div>

          <div className="card-flat overflow-hidden">
            <div className="px-5 py-3 border-b border-[var(--divider)] text-xs tracking-[0.2em] uppercase text-[var(--muted-text)]">
              {data.account}
              <span className="ml-2 normal-case tracking-normal text-[var(--muted-text)]">· showing {data.entries.length} of {data.pagination?.total || 0}</span>
            </div>
            {data.entries.length === 0 ? (
              <div data-testid="gl-empty" className="p-8 text-center text-sm text-[var(--muted-text)]">
                No transactions in this period for the selected account.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[var(--hover)]">
                    <tr className="text-xs uppercase tracking-wider text-[var(--muted-text)]">
                      {selectMode && (
                        <th className="py-2.5 px-4 w-10">
                          <button type="button" onClick={toggleAll} className="inline-flex" data-testid="gl-header-select">
                            {allSelected ? <CheckSquare className="w-4 h-4 text-[#2F4F4F]" /> : <Square className="w-4 h-4 text-[var(--muted-text)]" />}
                          </button>
                        </th>
                      )}
                      <th className="py-2.5 px-4 text-left w-28">Date</th>
                      <th className="py-2.5 px-4 text-left">Description</th>
                      <th className="py-2.5 px-4 text-left w-14">Ccy</th>
                      <th className="py-2.5 px-4 text-right w-32">Debit</th>
                      <th className="py-2.5 px-4 text-right w-32">Credit</th>
                      <th className="py-2.5 px-4 text-right w-36">Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {offset === 0 && (
                      <tr className="border-b border-[var(--divider)] bg-[var(--hover)]/40 italic text-[var(--muted-text)]">
                        {selectMode && <td className="py-2 px-4" />}
                        <td className="py-2 px-4">{fmtISO(start)}</td>
                        <td className="py-2 px-4">Opening balance</td>
                        <td className="py-2 px-4 text-[10px] font-mono">{currency}</td>
                        <td className="py-2 px-4 text-right">—</td>
                        <td className="py-2 px-4 text-right">—</td>
                        <td className="py-2 px-4 text-right font-mono-data">{formatCurrency(data.opening_balance, data?.currency || currency)}</td>
                      </tr>
                    )}
                    {visibleEntries.map((e) => {
                      const rowCcy = e.currency || currency;
                      const isSelected = selectedIds.has(e.id);
                      return (
                        <tr
                          key={e.id}
                          data-testid={`gl-entry-${e.id}`}
                          className={`border-b border-[var(--divider)] last:border-none ${isSelected ? "bg-[#F5EAE3]" : ""}`}
                        >
                          {selectMode && (
                            <td className="py-2 px-4">
                              <button type="button" onClick={() => toggleOne(e.id)} className="inline-flex" data-testid={`gl-select-${e.id}`}>
                                {isSelected ? <CheckSquare className="w-4 h-4 text-[#2F4F4F]" /> : <Square className="w-4 h-4 text-[var(--muted-text)]" />}
                              </button>
                            </td>
                          )}
                          <td className="py-2 px-4 whitespace-nowrap">{e.date}</td>
                          <td className="py-2 px-4">
                            <span className="capitalize text-[var(--muted-text)] mr-2">{e.type}</span>
                            {e.category && <span className="mr-2">· {e.category}</span>}
                            {e.note && <span className="text-[var(--muted-text)]">— {e.note}</span>}
                          </td>
                          <td className="py-2 px-4 text-[10px] font-mono">
                            <span className="px-1.5 py-0.5 rounded text-[#FDFCF8]"
                                  style={{ backgroundColor: rowCcy === "USD" ? "#1E5A96" : "#4C956C" }}>{rowCcy}</span>
                          </td>
                          <td className="py-2 px-4 text-right font-mono-data">{e.debit > 0 ? formatCurrency(e.debit, rowCcy) : ""}</td>
                          <td className="py-2 px-4 text-right font-mono-data">{e.credit > 0 ? formatCurrency(e.credit, rowCcy) : ""}</td>
                          <td className="py-2 px-4 text-right font-mono-data">{formatCurrency(e.running_balance_ccy != null ? e.running_balance_ccy : e.running_balance, rowCcy)}</td>
                        </tr>
                      );
                    })}
                    {/* Per-currency totals — one row per rail so debits/credits never sum across rails */}
                    {visibleCurrencyTotals.map((r) => (
                      <tr key={`tot-${r.currency}`} data-testid={`gl-total-${r.currency}`}
                          className="border-t-2 border-[#2F4F4F] font-semibold bg-[var(--hover)]">
                        <td colSpan={selectMode ? 3 : 2} className="py-3 px-4">Totals · {r.currency} rail</td>
                        <td className="py-3 px-4 text-[10px] font-mono">
                          <span className="px-1.5 py-0.5 rounded text-[#FDFCF8]"
                                style={{ backgroundColor: r.currency === "USD" ? "#1E5A96" : "#4C956C" }}>{r.currency}</span>
                        </td>
                        <td className="py-3 px-4 text-right font-mono-data">{formatCurrency(r.debit, r.currency)}</td>
                        <td className="py-3 px-4 text-right font-mono-data">{formatCurrency(r.credit, r.currency)}</td>
                        <td className="py-3 px-4 text-right font-mono-data">{formatCurrency(r.closing_balance, r.currency)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {data.pagination?.has_more && (
              <div className="border-t border-[var(--divider)] p-3 text-center">
                <Button
                  variant="outline"
                  size="sm"
                  data-testid="gl-load-more"
                  disabled={loading}
                  onClick={() => load(offset + GL_PAGE, true)}
                >
                  {loading ? "Loading…" : `Load more (${data.pagination.total - data.entries.length} left)`}
                </Button>
              </div>
            )}
          </div>
        </>
      )}

      <AlertDialog open={bulkDeleteOpen} onOpenChange={setBulkDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {selectedIds.size} entr{selectedIds.size === 1 ? "y" : "ies"}?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes the underlying transactions from every report. You'll have 5 seconds to undo.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="gl-bulk-delete-confirm" onClick={doBulkDelete} className="bg-[#E63946] hover:bg-[#c92e3b] text-white">
              Delete {selectedIds.size}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={moveOpen} onOpenChange={setMoveOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Move {selectedIds.size} to another entity</DialogTitle>
            <DialogDescription>
              Selected transactions will re-appear under the target entity's books.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Label className="text-[10px] uppercase tracking-widest text-[var(--muted-text)]">Target entity</Label>
            <Select value={moveTargetId} onValueChange={setMoveTargetId}>
              <SelectTrigger data-testid="gl-move-target"><SelectValue placeholder="Pick an entity" /></SelectTrigger>
              <SelectContent>
                {moveTargetEntities.map((e) => (
                  <SelectItem key={e.id} value={e.id}>
                    <span className="inline-flex items-center gap-2">
                      <span style={{ color: e.color }}>{e.icon}</span>{e.name}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={() => setMoveOpen(false)}>Cancel</Button>
            <Button
              data-testid="gl-move-confirm"
              disabled={!moveTargetId}
              onClick={doBulkMove}
              className="rounded-full bg-[#2F4F4F] hover:bg-[#213838] text-[#FDFCF8]"
            >
              Move {selectedIds.size}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={recatOpen} onOpenChange={setRecatOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Change {catLabel} for {selectedIds.size}</DialogTitle>
            <DialogDescription>Every selected entry adopts this {catLabel}.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Label className="text-[10px] uppercase tracking-widest text-[var(--muted-text)]">New {catLabel}</Label>
            <Select value={recatCategory} onValueChange={setRecatCategory}>
              <SelectTrigger data-testid="gl-recat-target"><SelectValue placeholder={`Pick a ${catLabel}`} /></SelectTrigger>
              <SelectContent>
                {["expense", "income", "saving"].map((t) => (
                  (categories[t] || []).length > 0 && (
                    <SelectGroup key={t}>
                      <SelectLabel className="capitalize">{t}</SelectLabel>
                      {(categories[t] || []).map((c) => (
                        <SelectItem key={`${t}-${c.id}`} value={c.name}>
                          <span className="inline-flex items-center gap-2">
                            <span style={{ color: c.color }}>{c.icon}</span>{c.name}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  )
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={() => setRecatOpen(false)}>Cancel</Button>
            <Button
              data-testid="gl-recat-confirm"
              disabled={!recatCategory}
              onClick={doBulkRecategorize}
              className="rounded-full bg-[#2F4F4F] hover:bg-[#213838] text-[#FDFCF8]"
            >
              Recategorize {selectedIds.size}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
